import { Children, cloneElement, useState, type MouseEvent } from 'react';
import { useTheme } from '@emotion/react';
import { Key, Warning } from '@phosphor-icons/react';
import { EventType } from 'common/EventConstants';
import { useApi } from 'client/lib/trpc';
import { useUser } from 'client/lib/User';
import { useDeploymentClientState } from 'client/state/DeploymentClientState';
import { AddSSHKeyDialog } from 'client/pages/profile/components/AddSSHKeyDialog';
import { DropdownMenu } from 'client/components/DropdownMenu';

interface StartingWarningDropdownProps {
  item: 'command' | 'container' | 'vscode';
  children: JSX.Element;
}

export const SshWarningDropdown = ({ item, children }: StartingWarningDropdownProps) => {
  const [sshOpen, setSshOpen] = useState(false);
  const deploymentID = useDeploymentClientState((state) => state.document?.id);
  const { user, updateSession } = useUser();
  const theme = useTheme();
  const api = useApi();

  const isSshKey = !!user?.sshKeyCount;

  const handleInactiveClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!deploymentID) return;

    api.status.eventCreate.mutate({
      eventTypeID: EventType.DEPLOYMENT_SSH_OPEN_INACTIVE,
      deploymentID,
      item,
    });
  };

  const menuItems = [
    <DropdownMenu.Banner
      key="error"
      title="You need an SSH key"
      subtitle="SSH connection to a deployment requires an SSH key. This key is used to verify it is you who is loggin in via SSH."
      icon={<Key />}
      ornament={<Warning color={theme.color.yellow[100]} />}
    />,
    <DropdownMenu.Button key="add" id="add" primary onSelect={() => setSshOpen(true)}>
      Add SSH key
    </DropdownMenu.Button>,
  ];

  const child = Children.only(children);

  const button = cloneElement(child, {
    ...child.props,
    onClick: isSshKey ? children.props.onClick : handleInactiveClick,
  });

  if (!isSshKey) {
    return (
      <>
        <DropdownMenu items={menuItems}>{button}</DropdownMenu>
        <AddSSHKeyDialog
          open={sshOpen}
          onSave={(sshKeys) => {
            setSshOpen(false);
            updateSession();
          }}
          onClose={() => setSshOpen(false)}
        />
      </>
    );
  }

  return button;
};
